/**
 * System Graph contract (backend -> frontend)
 *
 * Single source of truth for the shape of `/api/health/system-graph`
 * and its runtime validator.
 */

export const API_STATUS_VALUES = ['healthy', 'active', 'stale', 'error', 'idle'] as const;
export type ApiStatus = (typeof API_STATUS_VALUES)[number];

export const STATUS_VALUES = [...API_STATUS_VALUES, 'unknown'] as const;
export type Status = (typeof STATUS_VALUES)[number];

export const LANE_VALUES = [
  'sources',
  'data_store',
  'feature_layer',
  'decision_system',
  'orchestration',
  'output',
  'serving',
  'learning',
  // Legacy aliases (frontend tolerates these from older API responses)
  'ingestion',
  'execution',
  'surfaces',
  'other',
] as const;
export type Lane = (typeof LANE_VALUES)[number];

export const BAND_VALUES = [
  'sources',
  'ingestion',
  'data_stores',
  'decisions',
  'orchestration',
  'execution',
  'serving',
  'surfaces',
] as const;
export type Band = (typeof BAND_VALUES)[number];

export const TYPE_VALUES = [
  'source',
  'processor',
  'storage',
  'decision',
  'executor',
  'surface',
  'other',
] as const;
export type Type = (typeof TYPE_VALUES)[number];

export interface SystemNode {
  id: string;
  lane: Lane;
  label: string;
  sublabel: string | null;
  type: Type | null;
  status: Status;
  freshness_s: number | null;
  last_ok_at: string | null;
  /** Optional stale threshold in seconds, provided by backend (nullable). */
  stale_after_s: number | null;
  details: Record<string, unknown>;
  trace_id: string | null;
  /** Collapsible group this node belongs to (architecture mode). */
  group?: string | null;
  /** True when this node is a collapsed summary representing its group. */
  is_group_summary?: boolean;
  /** Band this node belongs to (v2 grouping, optional). */
  band?: Band | null;
}

/** Collapsible group definition for architecture mode. */
export interface SystemGroup {
  id: string;
  label: string;
  lane: Lane;
  /** Number of children when collapsed. */
  child_count: number;
  /** Worst status among children. */
  status: Status;
}

export interface SystemEdge {
  from: string;
  to: string;
  status: Status;
  freshness_s: number | null;
  latency_ms_p95: number | null;
  edge_type: string | null;
}

export interface SystemGraph {
  property_id: string | null;
  generated_at: string;
  nodes: SystemNode[];
  edges: SystemEdge[];
  groups?: SystemGroup[];
}

export class SystemGraphPayloadError extends Error {
  raw: unknown;
  constructor(message: string, raw: unknown) {
    super(message);
    this.name = 'SystemGraphPayloadError';
    this.raw = raw;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNodeLike(
  value: unknown,
): value is Record<string, unknown> & { id: string; lane: string; label: string } {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.lane === 'string' &&
    typeof value.label === 'string'
  );
}

function isEdgeLike(
  value: unknown,
): value is Record<string, unknown> & { from: string; to: string } {
  return isRecord(value) && typeof value.from === 'string' && typeof value.to === 'string';
}

function normalizeStatus(status: unknown, warnings: string[], context: string): Status {
  if (
    status === 'healthy' ||
    status === 'active' ||
    status === 'stale' ||
    status === 'error' ||
    status === 'idle'
  ) {
    return status;
  }
  if (status === null || status === undefined || status === 'null') return 'unknown';
  if (typeof status === 'string') warnings.push(`Unknown status "${status}" at ${context}`);
  return 'unknown';
}

function normalizeLane(lane: unknown, warnings: string[], context: string): Lane {
  if (typeof lane !== 'string') return 'other';
  if (
    lane === 'sources' ||
    lane === 'data_store' ||
    lane === 'feature_layer' ||
    lane === 'decision_system' ||
    lane === 'orchestration' ||
    lane === 'output' ||
    lane === 'serving' ||
    lane === 'learning' ||
    // Legacy aliases
    lane === 'ingestion' ||
    lane === 'execution' ||
    lane === 'surfaces'
  ) {
    return lane;
  }
  warnings.push(`Unknown lane "${lane}" at ${context}`);
  return 'other';
}

function normalizeBand(band: unknown): Band | null {
  if (band === null || band === undefined) return null;
  if (typeof band !== 'string') return null;
  if ((BAND_VALUES as readonly string[]).includes(band)) return band as Band;
  return null;
}

function normalizeType(type: unknown, warnings: string[], context: string): Type | null {
  if (type === null || type === undefined) return null;
  if (typeof type !== 'string') return null;
  if (
    type === 'source' ||
    type === 'processor' ||
    type === 'storage' ||
    type === 'decision' ||
    type === 'executor' ||
    type === 'surface'
  ) {
    return type;
  }
  warnings.push(`Unknown type "${type}" at ${context}`);
  return 'other';
}

function normalizeNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function normalizeString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

/**
 * Parse + validate unknown JSON into a normalized SystemGraph.
 *
 * - Throws SystemGraphPayloadError when required fields are missing
 * - Tolerates unknown statuses/lanes/types by mapping to `unknown`/`other`
 *   and returning warnings (for dev visibility).
 */
export function parseSystemGraphPayload(raw: unknown): { graph: SystemGraph; warnings: string[] } {
  if (!isRecord(raw))
    throw new SystemGraphPayloadError('Graph payload invalid (not an object)', raw);
  if (typeof raw.generated_at !== 'string') {
    throw new SystemGraphPayloadError('Graph payload invalid (missing generated_at)', raw);
  }
  if (!('property_id' in raw)) {
    throw new SystemGraphPayloadError('Graph payload invalid (missing property_id)', raw);
  }
  if (!Array.isArray(raw.nodes) || !Array.isArray(raw.edges)) {
    throw new SystemGraphPayloadError('Graph payload invalid (missing nodes/edges)', raw);
  }

  const warnings: string[] = [];

  const nodes: SystemNode[] = raw.nodes.filter(isNodeLike).map((n) => {
    const context = `node:${n.id}`;
    return {
      id: n.id,
      lane: normalizeLane(n.lane, warnings, context),
      label: n.label,
      sublabel: normalizeString(n.sublabel),
      type: normalizeType(n.type, warnings, context),
      status: normalizeStatus(n.status, warnings, context),
      freshness_s: normalizeNumber(n.freshness_s),
      last_ok_at: normalizeString(n.last_ok_at),
      stale_after_s: normalizeNumber(n.stale_after_s),
      details: isRecord(n.details) ? (n.details as Record<string, unknown>) : {},
      trace_id: normalizeString(n.trace_id),
      group: normalizeString(n.group),
      is_group_summary: n.is_group_summary === true,
      band: normalizeBand(n.band),
    };
  });

  const edges: SystemEdge[] = raw.edges.filter(isEdgeLike).map((e) => {
    const context = `edge:${e.from}->${e.to}`;
    return {
      from: e.from as string,
      to: e.to as string,
      status: normalizeStatus(e.status, warnings, context),
      freshness_s: normalizeNumber(e.freshness_s),
      latency_ms_p95: normalizeNumber(e.latency_ms_p95),
      edge_type: typeof e.edge_type === 'string' ? e.edge_type : null,
    };
  });

  return {
    graph: {
      property_id:
        raw.property_id === null || typeof raw.property_id === 'string' ? raw.property_id : null,
      generated_at: raw.generated_at,
      nodes,
      edges,
    },
    warnings,
  };
}
